import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../../firebase/config';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { ArrowLeft, Brain, CheckCircle, Info, Download, BookOpen, Target, TrendingUp, Building2, GraduationCap, Briefcase, Rocket } from 'lucide-react';
import jsPDF from 'jspdf';
import 'jspdf-autotable';

// Comprehensive Branch-Specific Subdomains
const BRANCH_SUBDOMAINS = {
  'Computer Engineering': [
    {
      id: 'software-dev',
      name: 'Software Development',
      description: 'Building applications and systems that solve real-world problems through code',
      details: 'Software developers design, create, test, and maintain computer programs. You\'ll work with programming languages, frameworks, and tools to build everything from mobile apps to enterprise systems.',
      skills: ['Programming', 'Problem Solving', 'Algorithms', 'Testing', 'Code Review'],
      careerPaths: ['Full Stack Developer', 'Backend Developer', 'Software Engineer', 'Application Developer'],
      governmentJobs: ['ISRO Scientist/Engineer', 'DRDO Scientist', 'NIC Technical Officer', 'State Govt IT Departments'],
      psus: ['BHEL (IT Division)', 'NTPC (IT)', 'ONGC (IT Services)', 'SAIL (IT)'],
      entrepreneurship: ['SaaS Product Development', 'Custom Software Solutions', 'App Development Agency', 'Software Consulting'],
      newAge: ['Low-code/No-code Platforms', 'Web3 Development', 'Quantum Computing', 'Edge Computing']
    },
    {
      id: 'data-science',
      name: 'Data Science & AI',
      description: 'Extracting insights from data and building intelligent systems',
      details: 'Data scientists analyze complex datasets to find patterns and make predictions. AI engineers build systems that can learn, reason, and make decisions.',
      skills: ['Machine Learning', 'Statistics', 'Python/R', 'Data Visualization', 'Deep Learning'],
      careerPaths: ['Data Scientist', 'ML Engineer', 'AI Researcher', 'Data Analyst', 'AI Consultant'],
      governmentJobs: ['DRDO AI Research', 'C-DAC Scientist', 'NITI Aayog Data Analyst', 'Smart Cities Mission'],
      psus: ['BSNL Analytics', 'Indian Railways Analytics', 'ONGC Data Science', 'Coal India Analytics'],
      entrepreneurship: ['AI-powered Product Development', 'Data Analytics Consulting', 'ML-as-a-Service', 'AI Training'],
      newAge: ['Generative AI', 'MLOps', 'Responsible AI', 'AutoML Platforms', 'Edge AI']
    },
    {
      id: 'cybersecurity',
      name: 'Cybersecurity',
      description: 'Protecting systems, networks, and data from digital attacks',
      details: 'Cybersecurity professionals safeguard digital assets by identifying vulnerabilities, implementing security measures, and responding to threats.',
      skills: ['Network Security', 'Ethical Hacking', 'Risk Analysis', 'Cryptography', 'Security Protocols'],
      careerPaths: ['Security Analyst', 'Penetration Tester', 'Security Architect', 'SOC Analyst', 'CISO'],
      governmentJobs: ['CERT-In', 'Cyber Police', 'Intelligence Bureau (Cyber)', 'NIC Security', 'Defence Cyber'],
      psus: ['BSNL Cybersecurity', 'NTPC Security', 'Banking PSU Security', 'Railway Cyber Security'],
      entrepreneurship: ['Security Consulting', 'Penetration Testing Services', 'Security Product Development', 'Training'],
      newAge: ['Cloud Security', 'IoT Security', 'Blockchain Security', 'Zero Trust Architecture', 'Bug Bounty']
    },
    {
      id: 'web-dev',
      name: 'Web Development',
      description: 'Creating interactive websites and web applications',
      details: 'Web developers build the websites and web apps we use daily, combining design, functionality, and user experience.',
      skills: ['HTML/CSS/JavaScript', 'Frontend Frameworks', 'Backend Development', 'APIs', 'Responsive Design'],
      careerPaths: ['Frontend Developer', 'Backend Developer', 'Full Stack Developer', 'Web Designer'],
      governmentJobs: ['NIC Web Developer', 'State IT Departments', 'Digital India Projects', 'E-Governance'],
      psus: ['IRCTC Web Team', 'PSU Bank Web Development', 'BSNL Web Services', 'Air India Digital'],
      entrepreneurship: ['Web Development Agency', 'E-commerce Platform', 'CMS Development', 'Web App SaaS'],
      newAge: ['Progressive Web Apps', 'Jamstack', 'WebAssembly', 'Web3', 'Serverless Web']
    },
    {
      id: 'mobile-dev',
      name: 'Mobile App Development',
      description: 'Building applications for smartphones and tablets',
      details: 'Mobile developers create apps for iOS, Android, or cross-platform, designing intuitive interfaces for devices everyone carries.',
      skills: ['Mobile Frameworks', 'App Design', 'API Integration', 'Performance Optimization', 'App Store Deployment'],
      careerPaths: ['iOS Developer', 'Android Developer', 'Flutter Developer', 'React Native Developer'],
      governmentJobs: ['Mobile App Development for Govt Services', 'Digital India Apps', 'MyGov Platform'],
      psus: ['Railway Mobile Apps', 'Banking Apps', 'Insurance Apps', 'Utility Company Apps'],
      entrepreneurship: ['App Development Studio', 'Mobile Game Development', 'App-based Startup', 'App Consulting'],
      newAge: ['5G App Development', 'AR/VR Mobile Apps', 'Foldable UI', 'App Clips', 'Super Apps']
    },
    {
      id: 'cloud-devops',
      name: 'Cloud Computing & DevOps',
      description: 'Managing infrastructure and automating deployment',
      details: 'DevOps engineers bridge development and operations, automating processes and managing cloud infrastructure.',
      skills: ['Cloud Platforms', 'CI/CD', 'Infrastructure as Code', 'Containers', 'Monitoring'],
      careerPaths: ['DevOps Engineer', 'Cloud Engineer', 'SRE', 'Platform Engineer', 'Cloud Architect'],
      governmentJobs: ['MeitY Cloud Services', 'NIC Cloud Infrastructure', 'Defence Cloud', 'State Data Centers'],
      psus: ['PSU Cloud Migration', 'Railway Cloud Services', 'Banking Cloud', 'Energy Sector Cloud'],
      entrepreneurship: ['Cloud Consulting', 'DevOps Services', 'Cloud Cost Optimization', 'Managed Services'],
      newAge: ['Multi-cloud Management', 'Kubernetes', 'GitOps', 'Infrastructure Automation', 'FinOps']
    },
    {
      id: 'iot-embedded',
      name: 'IoT & Embedded Systems',
      description: 'Connecting physical devices to digital networks',
      details: 'IoT engineers design and develop smart devices and embedded systems that connect the physical and digital worlds.',
      skills: ['Embedded C/C++', 'Microcontrollers', 'Sensors', 'Communication Protocols', 'RTOS'],
      careerPaths: ['IoT Engineer', 'Embedded Developer', 'Firmware Engineer', 'Hardware Designer'],
      governmentJobs: ['ISRO Embedded Systems', 'DRDO Electronics', 'Smart Cities IoT', 'Agriculture IoT'],
      psus: ['BHEL Embedded', 'HAL Electronics', 'BEL Systems', 'Railway Signaling Systems'],
      entrepreneurship: ['Smart Home Solutions', 'Industrial IoT', 'Agricultural Tech', 'IoT Consulting'],
      newAge: ['Edge Computing', 'IIoT (Industrial IoT)', 'Smart Cities', 'Wearable Tech', '5G IoT']
    },
    {
      id: 'blockchain',
      name: 'Blockchain Technology',
      description: 'Building decentralized applications and systems',
      details: 'Blockchain developers create distributed ledger solutions, smart contracts, and decentralized applications.',
      skills: ['Smart Contracts', 'Solidity', 'Cryptography', 'Distributed Systems', 'Web3'],
      careerPaths: ['Blockchain Developer', 'Smart Contract Developer', 'DApp Developer', 'Blockchain Consultant'],
      governmentJobs: ['NITI Aayog Blockchain', 'Land Records Blockchain', 'Voting System Development'],
      psus: ['Banking Blockchain', 'Supply Chain Blockchain', 'Digital Currency Projects'],
      entrepreneurship: ['Blockchain Consulting', 'DApp Development', 'NFT Platform', 'Crypto Solutions'],
      newAge: ['DeFi', 'NFTs', 'DAOs', 'Layer 2 Solutions', 'Cross-chain Interoperability']
    }
  ],
  'Computer Science and Engineering': [
    // All CE domains plus:
    {
      id: 'algorithms-research',
      name: 'Algorithms & Research',
      description: 'Advancing computational theory and solving complex problems',
      details: 'Researchers work on fundamental computer science problems, developing new algorithms and computational methods.',
      skills: ['Algorithms', 'Complexity Theory', 'Mathematics', 'Research Methodology', 'Publication'],
      careerPaths: ['Research Scientist', 'Algorithm Engineer', 'Professor', 'PhD Scholar'],
      governmentJobs: ['TIFR Research', 'IIT/NIT Faculty', 'CSIR Scientist', 'DRDO Research'],
      psus: ['ISRO Algorithm Development', 'BARC Computing', 'C-DAC Research'],
      entrepreneurship: ['Research Consulting', 'Algorithm-as-a-Service', 'Academic Publishing'],
      newAge: ['Quantum Algorithms', 'Neuromorphic Computing', 'DNA Computing', 'Approximate Computing']
    },
    {
      id: 'computer-vision',
      name: 'Computer Vision',
      description: 'Teaching computers to see and understand images',
      details: 'Computer vision engineers develop systems that can interpret and understand visual information from the world.',
      skills: ['Deep Learning', 'Image Processing', 'Neural Networks', 'OpenCV', 'Pattern Recognition'],
      careerPaths: ['CV Engineer', 'Vision Scientist', 'AR/VR Developer', 'Robotics Vision Engineer'],
      governmentJobs: ['Defence Surveillance', 'Smart City Monitoring', 'ISRO Image Analysis', 'Police Forensics'],
      psus: ['Railway Safety Systems', 'BHEL Vision Systems', 'Nuclear Plant Monitoring'],
      entrepreneurship: ['Vision-based Products', 'Surveillance Solutions', 'Medical Imaging', 'Quality Control'],
      newAge: ['3D Vision', 'Video Understanding', 'Visual AI', 'Autonomous Systems', 'AR/VR']
    },
    {
      id: 'nlp',
      name: 'Natural Language Processing',
      description: 'Enabling computers to understand human language',
      details: 'NLP engineers build systems that can understand, interpret, and generate human language.',
      skills: ['Text Processing', 'Machine Learning', 'Linguistics', 'Deep Learning', 'Transformers'],
      careerPaths: ['NLP Engineer', 'Conversational AI Engineer', 'Linguist Engineer', 'ML Engineer'],
      governmentJobs: ['Regional Language Computing', 'Translation Services', 'Speech Recognition'],
      psus: ['Voice Banking Systems', 'Customer Service AI', 'Document Processing'],
      entrepreneurship: ['Chatbot Development', 'Translation Services', 'Content Generation', 'Voice Tech'],
      newAge: ['Large Language Models', 'Multimodal AI', 'Code Generation', 'Conversational AI']
    }
  ],
  'Electrical Engineering': [
    {
      id: 'power-systems',
      name: 'Power Systems Engineering',
      description: 'Designing and managing electrical power generation and distribution',
      details: 'Power systems engineers work on the generation, transmission, and distribution of electrical power.',
      skills: ['Power Systems Analysis', 'Protection Systems', 'SCADA', 'Grid Management', 'Power Electronics'],
      careerPaths: ['Power Systems Engineer', 'Grid Engineer', 'Protection Engineer', 'Energy Consultant'],
      governmentJobs: ['Power Grid Corporation', 'State Electricity Boards', 'CEA', 'CPRI'],
      psus: ['NTPC', 'NHPC', 'PGCIL', 'Power Finance Corporation', 'Rural Electrification Corporation'],
      entrepreneurship: ['Solar EPC', 'Energy Consulting', 'Microgrid Solutions', 'Power Quality Services'],
      newAge: ['Smart Grids', 'Virtual Power Plants', 'Grid-scale Storage', 'Demand Response', 'V2G']
    },
    {
      id: 'renewable-energy',
      name: 'Renewable Energy',
      description: 'Harnessing clean energy from sun, wind, and other sources',
      details: 'Renewable energy engineers design and implement sustainable energy solutions using solar, wind, hydro, and other clean sources.',
      skills: ['Solar PV Design', 'Wind Energy', 'Energy Storage', 'Grid Integration', 'Project Management'],
      careerPaths: ['Solar Engineer', 'Wind Energy Engineer', 'Energy Analyst', 'Sustainability Consultant'],
      governmentJobs: ['MNRE', 'SECI', 'IREDA', 'State Renewable Energy Agencies'],
      psus: ['NTPC Renewable', 'NHPC', 'SJVN', 'THDC', 'NLC India'],
      entrepreneurship: ['Solar Installation', 'Energy Storage Solutions', 'Green Consulting', 'Rooftop Solar'],
      newAge: ['Green Hydrogen', 'Offshore Wind', 'Floating Solar', 'Advanced Storage', 'Microgrids']
    },
    {
      id: 'control-automation',
      name: 'Control Systems & Automation',
      description: 'Automating processes and controlling complex systems',
      details: 'Control engineers design automated systems for manufacturing, robotics, and industrial processes.',
      skills: ['PLC Programming', 'SCADA', 'Industrial Automation', 'Process Control', 'Robotics'],
      careerPaths: ['Automation Engineer', 'Control Systems Engineer', 'Process Engineer', 'Robotics Engineer'],
      governmentJobs: ['BHEL Automation', 'Railway Signaling', 'Defence Systems', 'Nuclear Plants'],
      psus: ['BHEL', 'NTPC', 'ONGC', 'IOCL', 'Steel Authority of India'],
      entrepreneurship: ['Industrial Automation Solutions', 'PLC Services', 'Smart Manufacturing', 'Robotics'],
      newAge: ['Industry 4.0', 'Cobots', 'Predictive Maintenance', 'Digital Twins', 'Edge Automation']
    },
    {
      id: 'electronics-vlsi',
      name: 'Electronics & VLSI Design',
      description: 'Designing integrated circuits and electronic systems',
      details: 'VLSI engineers design chips and electronic circuits that power all modern devices.',
      skills: ['VLSI Design', 'Verilog/VHDL', 'Circuit Design', 'CAD Tools', 'Semiconductor Physics'],
      careerPaths: ['VLSI Design Engineer', 'Chip Design Engineer', 'Verification Engineer', 'Analog Designer'],
      governmentJobs: ['ISRO Electronics', 'DRDO Electronics', 'SAMEER', 'C-DOT'],
      psus: ['BEL', 'ECIL', 'ITI Limited', 'Semiconductor Complex Limited'],
      entrepreneurship: ['VLSI Design Services', 'Chip Consulting', 'ASIC Design', 'IP Core Development'],
      newAge: ['Advanced Node Design', 'Chiplets', 'Neuromorphic Chips', '3D ICs', 'Quantum Chips']
    },
    {
      id: 'electric-vehicles',
      name: 'Electric Vehicle Technology',
      description: 'Developing electrical systems for future mobility',
      details: 'EV engineers work on battery systems, motor drives, charging infrastructure, and vehicle electronics.',
      skills: ['Battery Management', 'Motor Drives', 'Power Electronics', 'Charging Systems', 'Vehicle Electronics'],
      careerPaths: ['EV Engineer', 'Battery Engineer', 'Charging Infrastructure Engineer', 'Power Electronics Engineer'],
      governmentJobs: ['EESL', 'State EV Policies', 'Transport Corporations', 'Energy Efficiency Bureau'],
      psus: ['BHEL EV Division', 'Energy Efficiency Services Ltd', 'State Transport Corporations'],
      entrepreneurship: ['EV Charging Stations', 'Battery Swapping', 'EV Conversion', 'EV Components'],
      newAge: ['Fast Charging', 'Wireless Charging', 'V2G', 'Battery Recycling', 'Autonomous EVs']
    },
    {
      id: 'telecommunications',
      name: 'Telecommunications',
      description: 'Building communication networks and systems',
      details: 'Telecom engineers design, implement, and maintain communication networks including 5G and satellite systems.',
      skills: ['RF Engineering', '5G/4G', 'Network Planning', 'Wireless Communication', 'Optical Networks'],
      careerPaths: ['Telecom Engineer', 'RF Engineer', 'Network Engineer', '5G Specialist'],
      governmentJobs: ['DoT', 'BSNL', 'MTNL', 'TRAI', 'Telecom PSUs'],
      psus: ['BSNL', 'MTNL', 'ITI Limited', 'Tejas Networks', 'C-DOT'],
      entrepreneurship: ['ISP Services', 'Network Consulting', 'Telecom Infrastructure', 'IoT Connectivity'],
      newAge: ['5G', '6G Research', 'Satellite Internet', 'Private 5G Networks', 'Open RAN']
    },
    {
      id: 'instrumentation',
      name: 'Instrumentation & Measurement',
      description: 'Designing sensors and measurement systems',
      details: 'Instrumentation engineers develop sensors, measurement systems, and monitoring equipment for various industries.',
      skills: ['Sensor Technology', 'Signal Processing', 'Data Acquisition', 'Calibration', 'Process Instrumentation'],
      careerPaths: ['Instrumentation Engineer', 'Measurement Engineer', 'Process Control Engineer', 'Test Engineer'],
      governmentJobs: ['BARC', 'ISRO', 'NPL', 'CSIR Labs', 'Defence R&D'],
      psus: ['BHEL', 'IOCL', 'ONGC', 'Nuclear Plants', 'Refineries'],
      entrepreneurship: ['Instrumentation Services', 'Sensor Manufacturing', 'Calibration Lab', 'IoT Sensors'],
      newAge: ['Smart Sensors', 'Wireless Sensing', 'Nano-sensors', 'Optical Sensors', 'MEMS']
    },
    {
      id: 'biomedical-electronics',
      name: 'Biomedical Electronics',
      description: 'Developing medical devices and healthcare technology',
      details: 'Biomedical engineers create medical equipment, diagnostic devices, and healthcare technology.',
      skills: ['Medical Devices', 'Signal Processing', 'Imaging Systems', 'Regulatory Standards', 'Biosensors'],
      careerPaths: ['Biomedical Engineer', 'Clinical Engineer', 'Medical Device Designer', 'Healthcare Tech Engineer'],
      governmentJobs: ['AIIMS/Govt Hospital Tech', 'DRDO Biomedical', 'Medical Device Regulation'],
      psus: ['HLL Lifecare', 'Indian Drugs & Pharmaceuticals', 'Medical Equipment PSUs'],
      entrepreneurship: ['Medical Device Startup', 'Diagnostic Equipment', 'Healthcare IoT', 'Telemedicine Tech'],
      newAge: ['Wearable Health Tech', 'AI Diagnostics', 'Remote Patient Monitoring', 'Surgical Robotics']
    }
  ],
  'Mechanical Engineering': [
    {
      id: 'automotive',
      name: 'Automotive Engineering',
      description: 'Designing vehicles and automotive systems',
      details: 'Automotive engineers develop cars, trucks, and other vehicles, focusing on design, performance, and safety.',
      skills: ['Vehicle Dynamics', 'CAD/CAM', 'Powertrain', 'Safety Systems', 'Manufacturing'],
      careerPaths: ['Automotive Engineer', 'Design Engineer', 'Vehicle Testing Engineer', 'R&D Engineer'],
      governmentJobs: ['ARAI', 'VRDE', 'Transport Research Wing', 'State Transport'],
      psus: ['Maruti Suzuki', 'BEML', 'Bharat Earth Movers', 'State Transport Corporations'],
      entrepreneurship: ['Auto Component Manufacturing', 'EV Conversion', 'Automotive Services', 'Testing Services'],
      newAge: ['Electric Vehicles', 'Autonomous Driving', 'Connected Cars', 'Advanced Materials', 'Lightweighting']
    },
    {
      id: 'aerospace',
      name: 'Aerospace Engineering',
      description: 'Designing aircraft, spacecraft, and defense systems',
      details: 'Aerospace engineers work on aircraft, satellites, missiles, and space vehicles.',
      skills: ['Aerodynamics', 'Flight Mechanics', 'Propulsion', 'Structures', 'CFD'],
      careerPaths: ['Aerospace Engineer', 'Aircraft Designer', 'Propulsion Engineer', 'Flight Test Engineer'],
      governmentJobs: ['ISRO', 'DRDO', 'HAL', 'NAL', 'ADA'],
      psus: ['HAL', 'BEL', 'BDL', 'GRSE', 'MDL'],
      entrepreneurship: ['Drone Manufacturing', 'Aerospace Components', 'Defense Consulting', 'UAV Services'],
      newAge: ['Commercial Space', 'Drones/UAVs', 'Urban Air Mobility', 'Hypersonics', 'Reusable Rockets']
    },
    {
      id: 'manufacturing',
      name: 'Manufacturing & Production',
      description: 'Optimizing production processes and systems',
      details: 'Manufacturing engineers focus on efficient production, quality control, and process optimization.',
      skills: ['Production Planning', 'Quality Control', 'Lean Manufacturing', 'Six Sigma', 'Supply Chain'],
      careerPaths: ['Manufacturing Engineer', 'Production Manager', 'Quality Engineer', 'Operations Manager'],
      governmentJobs: ['Ordnance Factories', 'Defence Manufacturing', 'PSU Manufacturing Units'],
      psus: ['BHEL', 'HMT', 'BEML', 'SAIL', 'HAL'],
      entrepreneurship: ['Contract Manufacturing', 'Component Production', 'Quality Services', '3D Printing Services'],
      newAge: ['Industry 4.0', 'Additive Manufacturing', 'Smart Factories', 'Digital Manufacturing', 'Lights-out Manufacturing']
    },
    {
      id: 'robotics',
      name: 'Robotics & Mechatronics',
      description: 'Building intelligent machines and automated systems',
      details: 'Robotics engineers design robots and automated systems for manufacturing, healthcare, and other applications.',
      skills: ['Robot Design', 'Kinematics', 'Control Systems', 'Machine Vision', 'Programming'],
      careerPaths: ['Robotics Engineer', 'Automation Engineer', 'Mechatronics Engineer', 'Systems Integrator'],
      governmentJobs: ['DRDO Robotics', 'ISRO Robotics', 'Defence Robotics', 'Industrial Automation'],
      psus: ['BHEL Automation', 'NTPC Robotics', 'Nuclear Plants Robotics'],
      entrepreneurship: ['Industrial Robotics', 'Service Robots', 'Educational Robotics', 'Agricultural Robots'],
      newAge: ['Collaborative Robots', 'AI Robotics', 'Soft Robotics', 'Swarm Robotics', 'Bio-inspired Robots']
    },
    {
      id: 'thermal',
      name: 'Thermal & Energy Engineering',
      description: 'Managing heat, energy, and power systems',
      details: 'Thermal engineers work on HVAC systems, power plants, engines, and energy conversion.',
      skills: ['Thermodynamics', 'Heat Transfer', 'HVAC', 'Energy Systems', 'CFD'],
      careerPaths: ['Thermal Engineer', 'HVAC Engineer', 'Energy Engineer', 'Power Plant Engineer'],
      governmentJobs: ['NTPC', 'Nuclear Plants', 'Thermal Power Stations', 'Energy Efficiency Bureau'],
      psus: ['NTPC', 'NHPC', 'BHEL', 'Nuclear Power Corporation', 'Coal India'],
      entrepreneurship: ['HVAC Consulting', 'Energy Auditing', 'Solar Thermal', 'Refrigeration Services'],
      newAge: ['Waste Heat Recovery', 'Thermal Storage', 'High-efficiency Systems', 'Sustainable HVAC']
    },
    {
      id: 'cad-design',
      name: 'CAD/CAM & Product Design',
      description: 'Creating and designing products using computer tools',
      details: 'Design engineers use CAD software to create detailed designs and simulations of products.',
      skills: ['CAD Software', '3D Modeling', 'FEA', 'Design for Manufacturing', 'Prototyping'],
      careerPaths: ['Design Engineer', 'CAD Engineer', 'Product Designer', 'Simulation Engineer'],
      governmentJobs: ['ISRO Design', 'DRDO Design', 'PSU Design Departments', 'Defence Design'],
      psus: ['BHEL', 'HAL', 'BEL', 'BEML', 'L&T'],
      entrepreneurship: ['Design Consulting', 'Prototyping Services', 'Product Development', '3D Printing Services'],
      newAge: ['Generative Design', 'Digital Twins', 'VR Design', 'Cloud CAD', 'AI-assisted Design']
    },
    {
      id: 'materials',
      name: 'Materials Engineering',
      description: 'Developing and testing advanced materials',
      details: 'Materials engineers work on developing new materials and improving existing ones for various applications.',
      skills: ['Material Science', 'Testing', 'Composites', 'Metallurgy', 'Failure Analysis'],
      careerPaths: ['Materials Engineer', 'Metallurgist', 'Quality Control Engineer', 'R&D Engineer'],
      governmentJobs: ['DMRL', 'ARCI', 'BARC', 'ISRO Materials', 'DRDO Materials'],
      psus: ['SAIL', 'NMDC', 'RINL', 'MOIL', 'HCL'],
      entrepreneurship: ['Material Testing Lab', 'Composite Manufacturing', 'Surface Treatment', 'Consulting'],
      newAge: ['Nanomaterials', 'Smart Materials', 'Biomaterials', 'Advanced Composites', '3D Printed Materials']
    },
    {
      id: 'fluid-mechanics',
      name: 'Fluid Mechanics & Hydraulics',
      description: 'Working with fluids, pumps, and hydraulic systems',
      details: 'Fluid mechanics engineers design systems involving fluids, including pumps, turbines, and hydraulic systems.',
      skills: ['Fluid Dynamics', 'CFD', 'Pump Design', 'Piping Systems', 'Turbomachinery'],
      careerPaths: ['Fluid Systems Engineer', 'Hydraulics Engineer', 'CFD Engineer', 'Turbine Engineer'],
      governmentJobs: ['Irrigation Departments', 'Water Resources', 'NHPC', 'Port Authorities'],
      psus: ['NHPC', 'NTPC', 'ONGC', 'IOCL', 'Water Authorities'],
      entrepreneurship: ['Pump Services', 'Hydraulic Systems', 'Water Management', 'CFD Consulting'],
      newAge: ['Computational Fluid Dynamics', 'Micro-fluidics', 'Advanced Turbomachinery', 'Smart Water Systems']
    }
  ]
};

// Add CSE all CE domains
BRANCH_SUBDOMAINS['Computer Science and Engineering'] = [
  ...BRANCH_SUBDOMAINS['Computer Engineering'],
  ...BRANCH_SUBDOMAINS['Computer Science and Engineering']
];

// Career Path Categories
const CAREER_CATEGORIES = {
  corporate: {
    name: 'Corporate/Private Sector',
    icon: '🏢',
    description: 'Jobs in private companies, startups, and MNCs'
  },
  government: {
    name: 'Government Jobs',
    icon: '🏛️',
    description: 'Central and state government positions'
  },
  psu: {
    name: 'Public Sector Undertakings',
    icon: '🏭',
    description: 'Government-owned corporations and companies'
  },
  entrepreneurship: {
    name: 'Entrepreneurship',
    icon: '🚀',
    description: 'Starting your own venture or business'
  },
  research: {
    name: 'Teaching & Research',
    icon: '🎓',
    description: 'Academic positions and research roles'
  },
  newAge: {
    name: 'New Age Opportunities',
    icon: '⚡',
    description: 'Emerging technologies and future careers'
  }
};

// 35 Comprehensive Questions
const QUESTIONS = [
  // Work Style & Preferences (8 questions)
  {
    id: 1,
    category: 'work-style',
    question: 'What type of work environment appeals to you most?',
    options: [
      { value: 'A', text: 'Dynamic corporate environment with growth opportunities', traits: ['software-dev', 'data-science', 'automotive', 'power-systems'], category: 'corporate' },
      { value: 'B', text: 'Stable government position with job security', traits: ['power-systems', 'government-sector', 'telecommunications', 'manufacturing'], category: 'government' },
      { value: 'C', text: 'Entrepreneurial setting where I create something of my own', traits: ['web-dev', 'mobile-dev', 'entrepreneurship', 'renewable-energy'], category: 'entrepreneurship' },
      { value: 'D', text: 'Academic/research environment focused on innovation', traits: ['algorithms-research', 'aerospace', 'materials', 'research'], category: 'research' }
    ]
  },
  {
    id: 2,
    category: 'work-style',
    question: 'How do you prefer to work on projects?',
    options: [
      { value: 'A', text: 'Hands-on building and implementation', traits: ['software-dev', 'web-dev', 'mobile-dev', 'manufacturing', 'automotive'] },
      { value: 'B', text: 'Analysis, planning, and strategic thinking', traits: ['data-science', 'algorithms-research', 'power-systems', 'cad-design'] },
      { value: 'C', text: 'Securing and protecting systems/processes', traits: ['cybersecurity', 'control-automation', 'instrumentation'] },
      { value: 'D', text: 'Designing and creating user experiences', traits: ['web-dev', 'mobile-dev', 'cad-design', 'automotive'] }
    ]
  },
  {
    id: 3,
    category: 'problem-solving',
    question: 'When solving a technical problem, you prefer:',
    options: [
      { value: 'A', text: 'Writing code or building solutions digitally', traits: ['software-dev', 'web-dev', 'mobile-dev', 'blockchain', 'nlp'] },
      { value: 'B', text: 'Analyzing data and finding patterns', traits: ['data-science', 'computer-vision', 'power-systems'] },
      { value: 'C', text: 'Working with physical systems and hardware', traits: ['iot-embedded', 'robotics', 'automotive', 'aerospace', 'electronics-vlsi'] },
      { value: 'D', text: 'Optimizing processes and workflows', traits: ['cloud-devops', 'manufacturing', 'control-automation'] }
    ]
  },
  {
    id: 4,
    category: 'interest',
    question: 'Which area excites you the most?',
    options: [
      { value: 'A', text: 'Software, apps, and digital products', traits: ['software-dev', 'web-dev', 'mobile-dev', 'blockchain'] },
      { value: 'B', text: 'Data, AI, and machine learning', traits: ['data-science', 'computer-vision', 'nlp', 'algorithms-research'] },
      { value: 'C', text: 'Electronics, circuits, and embedded systems', traits: ['iot-embedded', 'electronics-vlsi', 'biomedical-electronics', 'control-automation'] },
      { value: 'D', text: 'Machines, vehicles, and mechanical systems', traits: ['automotive', 'aerospace', 'robotics', 'manufacturing'] }
    ]
  },
  {
    id: 5,
    category: 'work-preference',
    question: 'Your ideal day at work involves:',
    options: [
      { value: 'A', text: 'Coding, debugging, and building features', traits: ['software-dev', 'web-dev', 'mobile-dev'] },
      { value: 'B', text: 'Analyzing systems and optimizing performance', traits: ['data-science', 'cloud-devops', 'power-systems', 'thermal'] },
      { value: 'C', text: 'Designing and testing physical systems', traits: ['automotive', 'aerospace', 'robotics', 'cad-design'] },
      { value: 'D', text: 'Managing projects and coordinating teams', traits: ['manufacturing', 'power-systems', 'telecommunications'] }
    ]
  },
  {
    id: 6,
    category: 'job-security',
    question: 'What matters most to you in a career?',
    options: [
      { value: 'A', text: 'High salary and rapid career growth', traits: ['software-dev', 'data-science', 'cloud-devops'], category: 'corporate' },
      { value: 'B', text: 'Job security and work-life balance', traits: ['power-systems', 'telecommunications', 'manufacturing'], category: 'government' },
      { value: 'C', text: 'Autonomy and building my own venture', traits: ['web-dev', 'mobile-dev', 'renewable-energy'], category: 'entrepreneurship' },
      { value: 'D', text: 'Contributing to research and innovation', traits: ['algorithms-research', 'aerospace', 'materials'], category: 'research' }
    ]
  },
  {
    id: 7,
    category: 'sector-preference',
    question: 'Which sector appeals to you?',
    options: [
      { value: 'A', text: 'Private companies and startups', traits: ['software-dev', 'data-science', 'web-dev'], category: 'corporate' },
      { value: 'B', text: 'Government departments and organizations', traits: ['power-systems', 'telecommunications'], category: 'government' },
      { value: 'C', text: 'Public Sector Undertakings (PSUs)', traits: ['power-systems', 'aerospace', 'manufacturing'], category: 'psu' },
      { value: 'D', text: 'Starting my own company', traits: ['web-dev', 'mobile-dev', 'renewable-energy'], category: 'entrepreneurship' }
    ]
  },
  {
    id: 8,
    category: 'impact',
    question: 'How do you want to make an impact?',
    options: [
      { value: 'A', text: 'Creating products used by millions', traits: ['software-dev', 'web-dev', 'mobile-dev', 'automotive'] },
      { value: 'B', text: 'Serving the nation through government work', traits: ['power-systems', 'aerospace', 'telecommunications'], category: 'government' },
      { value: 'C', text: 'Building infrastructure for the country', traits: ['power-systems', 'renewable-energy', 'telecommunications'], category: 'psu' },
      { value: 'D', text: 'Advancing knowledge through research', traits: ['algorithms-research', 'materials', 'aerospace'], category: 'research' }
    ]
  },

  // Technical Interests (10 questions)
  {
    id: 9,
    category: 'technical',
    question: 'Which technology interests you most?',
    options: [
      { value: 'A', text: 'Artificial Intelligence and Machine Learning', traits: ['data-science', 'computer-vision', 'nlp'] },
      { value: 'B', text: 'Internet of Things and Smart Devices', traits: ['iot-embedded', 'control-automation', 'telecommunications'] },
      { value: 'C', text: 'Renewable Energy and Sustainability', traits: ['renewable-energy', 'electric-vehicles', 'power-systems'] },
      { value: 'D', text: 'Robotics and Automation', traits: ['robotics', 'control-automation', 'manufacturing'] }
    ]
  },
  {
    id: 10,
    category: 'technical',
    question: 'What type of system do you want to work with?',
    options: [
      { value: 'A', text: 'Software systems and applications', traits: ['software-dev', 'web-dev', 'mobile-dev', 'cloud-devops'] },
      { value: 'B', text: 'Electronic circuits and embedded systems', traits: ['iot-embedded', 'electronics-vlsi', 'biomedical-electronics'] },
      { value: 'C', text: 'Mechanical systems and machines', traits: ['automotive', 'aerospace', 'robotics', 'manufacturing'] },
      { value: 'D', text: 'Electrical power and energy systems', traits: ['power-systems', 'renewable-energy', 'electric-vehicles'] }
    ]
  },
  {
    id: 11,
    category: 'technical',
    question: 'Which field of mathematics appeals to you?',
    options: [
      { value: 'A', text: 'Statistics, probability, and data analysis', traits: ['data-science', 'algorithms-research', 'computer-vision'] },
      { value: 'B', text: 'Calculus and differential equations', traits: ['power-systems', 'control-automation', 'thermal', 'fluid-mechanics'] },
      { value: 'C', text: 'Logic, discrete math, and algorithms', traits: ['software-dev', 'cybersecurity', 'blockchain', 'algorithms-research'] },
      { value: 'D', text: 'Applied mathematics and simulations', traits: ['cad-design', 'aerospace', 'automotive', 'materials'] }
    ]
  },
  {
    id: 12,
    category: 'tools',
    question: 'Which tools/software interest you?',
    options: [
      { value: 'A', text: 'Programming languages and development tools', traits: ['software-dev', 'web-dev', 'mobile-dev', 'blockchain'] },
      { value: 'B', text: 'CAD/CAM and simulation software', traits: ['cad-design', 'automotive', 'aerospace', 'manufacturing'] },
      { value: 'C', text: 'Circuit design and PCB tools', traits: ['electronics-vlsi', 'iot-embedded', 'biomedical-electronics'] },
      { value: 'D', text: 'Data analysis and ML frameworks', traits: ['data-science', 'computer-vision', 'nlp', 'algorithms-research'] }
    ]
  },
  {
    id: 13,
    category: 'scale',
    question: 'What scale of impact interests you?',
    options: [
      { value: 'A', text: 'Global - affecting millions worldwide', traits: ['software-dev', 'web-dev', 'mobile-dev', 'data-science'] },
      { value: 'B', text: 'National - serving the country', traits: ['power-systems', 'renewable-energy', 'aerospace', 'telecommunications'], category: 'government' },
      { value: 'C', text: 'Industrial - transforming industries', traits: ['manufacturing', 'automotive', 'robotics', 'control-automation'], category: 'psu' },
      { value: 'D', text: 'Research - advancing human knowledge', traits: ['algorithms-research', 'materials', 'aerospace'], category: 'research' }
    ]
  },
  {
    id: 14,
    category: 'innovation',
    question: 'How do you view innovation?',
    options: [
      { value: 'A', text: 'Creating new software products and services', traits: ['software-dev', 'web-dev', 'mobile-dev', 'blockchain'], category: 'entrepreneurship' },
      { value: 'B', text: 'Developing cutting-edge technology', traits: ['data-science', 'computer-vision', 'electronics-vlsi'], category: 'newAge' },
      { value: 'C', text: 'Improving existing systems and processes', traits: ['manufacturing', 'power-systems', 'cloud-devops'], category: 'psu' },
      { value: 'D', text: 'Fundamental research and discovery', traits: ['algorithms-research', 'materials', 'aerospace'], category: 'research' }
    ]
  },
  {
    id: 15,
    category: 'emerging-tech',
    question: 'Which emerging technology excites you?',
    options: [
      { value: 'A', text: 'Blockchain and Web3', traits: ['blockchain', 'software-dev', 'cybersecurity'], category: 'newAge' },
      { value: 'B', text: 'Electric and Autonomous Vehicles', traits: ['electric-vehicles', 'automotive', 'control-automation'], category: 'newAge' },
      { value: 'C', text: 'Quantum Computing', traits: ['algorithms-research', 'data-science', 'computer-vision'], category: 'newAge' },
      { value: 'D', text: 'Space Technology and Satellites', traits: ['aerospace', 'telecommunications', 'iot-embedded'], category: 'newAge' }
    ]
  },
  {
    id: 16,
    category: 'application',
    question: 'Where do you want to apply technology?',
    options: [
      { value: 'A', text: 'Consumer products and services', traits: ['software-dev', 'web-dev', 'mobile-dev', 'automotive'] },
      { value: 'B', text: 'Healthcare and medical devices', traits: ['biomedical-electronics', 'data-science', 'robotics'] },
      { value: 'C', text: 'Energy and sustainability', traits: ['renewable-energy', 'power-systems', 'electric-vehicles'] },
      { value: 'D', text: 'Defence and aerospace', traits: ['aerospace', 'cybersecurity', 'electronics-vlsi', 'telecommunications'] }
    ]
  },
  {
    id: 17,
    category: 'complexity',
    question: 'What level of complexity do you enjoy?',
    options: [
      { value: 'A', text: 'Solving algorithmic and computational challenges', traits: ['algorithms-research', 'data-science', 'software-dev'] },
      { value: 'B', text: 'Managing complex physical systems', traits: ['power-systems', 'aerospace', 'control-automation'] },
      { value: 'C', text: 'Integrating multiple technologies', traits: ['iot-embedded', 'robotics', 'telecommunications'] },
      { value: 'D', text: 'Optimizing large-scale operations', traits: ['cloud-devops', 'manufacturing', 'power-systems'] }
    ]
  },
  {
    id: 18,
    category: 'future-focus',
    question: 'What future do you want to build?',
    options: [
      { value: 'A', text: 'Digital and connected world', traits: ['software-dev', 'web-dev', 'mobile-dev', 'iot-embedded'] },
      { value: 'B', text: 'Sustainable and green future', traits: ['renewable-energy', 'electric-vehicles', 'power-systems'] },
      { value: 'C', text: 'Automated and robotic world', traits: ['robotics', 'control-automation', 'manufacturing'] },
      { value: 'D', text: 'Space-faring civilization', traits: ['aerospace', 'telecommunications', 'algorithms-research'] }
    ]
  },

  // Career Path Preferences (8 questions)
  {
    id: 19,
    category: 'career-path',
    question: 'After graduation, you see yourself:',
    options: [
      { value: 'A', text: 'Joining a top tech company or startup', traits: ['software-dev', 'data-science', 'web-dev'], category: 'corporate' },
      { value: 'B', text: 'Preparing for government exams (GATE/ESE/PSU)', traits: ['power-systems', 'telecommunications', 'manufacturing'], category: 'government' },
      { value: 'C', text: 'Starting my own tech venture', traits: ['web-dev', 'mobile-dev', 'blockchain'], category: 'entrepreneurship' },
      { value: 'D', text: 'Pursuing higher studies (MS/PhD)', traits: ['algorithms-research', 'materials', 'aerospace'], category: 'research' }
    ]
  },
  {
    id: 20,
    category: 'exam-prep',
    question: 'Are you interested in preparing for competitive exams?',
    options: [
      { value: 'A', text: 'Yes, for GATE/PSU/Government Jobs', traits: ['power-systems', 'telecommunications', 'manufacturing'], category: 'government' },
      { value: 'B', text: 'Maybe, as a backup option', traits: ['software-dev', 'automotive', 'control-automation'] },
      { value: 'C', text: 'No, I prefer direct placement', traits: ['software-dev', 'web-dev', 'data-science'], category: 'corporate' },
      { value: 'D', text: 'Only for higher studies (GATE for MS)', traits: ['algorithms-research', 'computer-vision', 'aerospace'], category: 'research' }
    ]
  },
  {
    id: 21,
    category: 'startup',
    question: 'What do you think about entrepreneurship?',
    options: [
      { value: 'A', text: 'Very interested, want to start my own company', traits: ['web-dev', 'mobile-dev', 'renewable-energy'], category: 'entrepreneurship' },
      { value: 'B', text: 'Maybe after gaining some experience', traits: ['software-dev', 'data-science', 'automotive'] },
      { value: 'C', text: 'Prefer stable job, not interested in entrepreneurship', traits: ['power-systems', 'telecommunications', 'manufacturing'], category: 'government' },
      { value: 'D', text: 'Want to work in a startup, not start one', traits: ['software-dev', 'web-dev', 'data-science'], category: 'corporate' }
    ]
  },
  {
    id: 22,
    category: 'teaching',
    question: 'How do you feel about teaching/research?',
    options: [
      { value: 'A', text: 'Very interested in academic career', traits: ['algorithms-research', 'materials', 'aerospace'], category: 'research' },
      { value: 'B', text: 'Would like to teach part-time', traits: ['software-dev', 'power-systems', 'automotive'] },
      { value: 'C', text: 'Not interested in teaching', traits: ['web-dev', 'mobile-dev', 'manufacturing'], category: 'corporate' },
      { value: 'D', text: 'Interested in industry research', traits: ['data-science', 'electronics-vlsi', 'aerospace'] }
    ]
  },
  {
    id: 23,
    category: 'psu-interest',
    question: 'What\'s your view on PSU jobs?',
    options: [
      { value: 'A', text: 'Highly interested, preparing specifically for PSUs', traits: ['power-systems', 'telecommunications', 'manufacturing'], category: 'psu' },
      { value: 'B', text: 'Good backup option with decent salary', traits: ['automotive', 'control-automation', 'aerospace'] },
      { value: 'C', text: 'Not interested, prefer private sector', traits: ['software-dev', 'web-dev', 'data-science'], category: 'corporate' },
      { value: 'D', text: 'Interested in specific PSUs only', traits: ['aerospace', 'renewable-energy', 'telecommunications'], category: 'psu' }
    ]
  },
  {
    id: 24,
    category: 'location',
    question: 'Where do you want to work?',
    options: [
      { value: 'A', text: 'Major cities with tech hubs', traits: ['software-dev', 'web-dev', 'data-science'], category: 'corporate' },
      { value: 'B', text: 'Anywhere in India, location not important', traits: ['power-systems', 'telecommunications', 'manufacturing'], category: 'government' },
      { value: 'C', text: 'Open to international opportunities', traits: ['software-dev', 'data-science', 'aerospace'], category: 'corporate' },
      { value: 'D', text: 'Remote work preferred', traits: ['web-dev', 'mobile-dev', 'cloud-devops'] }
    ]
  },
  {
    id: 25,
    category: 'higher-studies',
    question: 'Plans for higher education?',
    options: [
      { value: 'A', text: 'MS/PhD from top universities', traits: ['algorithms-research', 'computer-vision', 'aerospace'], category: 'research' },
      { value: 'B', text: 'MBA after few years of experience', traits: ['software-dev', 'manufacturing', 'automotive'] },
      { value: 'C', text: 'Not planning, want to work', traits: ['web-dev', 'mobile-dev', 'telecommunications'], category: 'corporate' },
      { value: 'D', text: 'M.Tech through GATE', traits: ['power-systems', 'electronics-vlsi', 'control-automation'], category: 'government' }
    ]
  },
  {
    id: 26,
    category: 'salary-vs-growth',
    question: 'What\'s more important initially?',
    options: [
      { value: 'A', text: 'High starting salary', traits: ['software-dev', 'data-science', 'cloud-devops'], category: 'corporate' },
      { value: 'B', text: 'Job security and stability', traits: ['power-systems', 'telecommunications', 'manufacturing'], category: 'government' },
      { value: 'C', text: 'Learning and skill development', traits: ['algorithms-research', 'aerospace', 'materials'], category: 'research' },
      { value: 'D', text: 'Entrepreneurial experience', traits: ['web-dev', 'mobile-dev', 'blockchain'], category: 'entrepreneurship' }
    ]
  },

  // Personality & Skills (9 questions)
  {
    id: 27,
    category: 'personality',
    question: 'Your approach to learning new skills:',
    options: [
      { value: 'A', text: 'Self-taught through online resources', traits: ['software-dev', 'web-dev', 'data-science'] },
      { value: 'B', text: 'Structured courses and certifications', traits: ['cloud-devops', 'cybersecurity', 'power-systems'] },
      { value: 'C', text: 'Hands-on projects and experimentation', traits: ['mobile-dev', 'robotics', 'iot-embedded'] },
      { value: 'D', text: 'Academic research and papers', traits: ['algorithms-research', 'materials', 'aerospace'] }
    ]
  },
  {
    id: 28,
    category: 'teamwork',
    question: 'In team projects, you typically:',
    options: [
      { value: 'A', text: 'Lead and coordinate the team', traits: ['manufacturing', 'power-systems', 'cloud-devops'] },
      { value: 'B', text: 'Focus on technical implementation', traits: ['software-dev', 'web-dev', 'electronics-vlsi'] },
      { value: 'C', text: 'Analyze and optimize the approach', traits: ['data-science', 'algorithms-research', 'cad-design'] },
      { value: 'D', text: 'Ensure quality and testing', traits: ['cybersecurity', 'control-automation', 'materials'] }
    ]
  },
  {
    id: 29,
    category: 'creativity',
    question: 'Your creative outlet is:',
    options: [
      { value: 'A', text: 'Building software projects', traits: ['software-dev', 'web-dev', 'mobile-dev'] },
      { value: 'B', text: 'Designing mechanical systems', traits: ['cad-design', 'automotive', 'aerospace'] },
      { value: 'C', text: 'Working with electronics and circuits', traits: ['iot-embedded', 'electronics-vlsi', 'robotics'] },
      { value: 'D', text: 'Data visualization and analysis', traits: ['data-science', 'computer-vision', 'algorithms-research'] }
    ]
  },
  {
    id: 30,
    category: 'problem-type',
    question: 'What problems do you enjoy solving?',
    options: [
      { value: 'A', text: 'Abstract computational problems', traits: ['algorithms-research', 'data-science', 'software-dev'] },
      { value: 'B', text: 'Real-world engineering challenges', traits: ['automotive', 'aerospace', 'manufacturing'] },
      { value: 'C', text: 'User experience and interface issues', traits: ['web-dev', 'mobile-dev', 'cad-design'] },
      { value: 'D', text: 'Optimization and efficiency problems', traits: ['cloud-devops', 'power-systems', 'control-automation'] }
    ]
  },
  {
    id: 31,
    category: 'stress-handling',
    question: 'Under pressure, you excel at:',
    options: [
      { value: 'A', text: 'Quick debugging and problem-solving', traits: ['software-dev', 'web-dev', 'cybersecurity'] },
      { value: 'B', text: 'Systematic troubleshooting', traits: ['control-automation', 'telecommunications', 'manufacturing'] },
      { value: 'C', text: 'Data-driven decision making', traits: ['data-science', 'power-systems', 'algorithms-research'] },
      { value: 'D', text: 'Physical testing and validation', traits: ['automotive', 'aerospace', 'robotics'] }
    ]
  },
  {
    id: 32,
    category: 'continuous-learning',
    question: 'How do you stay updated with technology?',
    options: [
      { value: 'A', text: 'Building personal projects', traits: ['software-dev', 'web-dev', 'mobile-dev'] },
      { value: 'B', text: 'Reading research papers', traits: ['algorithms-research', 'data-science', 'materials'] },
      { value: 'C', text: 'Online courses and certifications', traits: ['cloud-devops', 'cybersecurity', 'power-systems'] },
      { value: 'D', text: 'Attending workshops and conferences', traits: ['aerospace', 'renewable-energy', 'telecommunications'] }
    ]
  },
  {
    id: 33,
    category: 'motivation',
    question: 'What drives you professionally?',
    options: [
      { value: 'A', text: 'Creating impactful products', traits: ['software-dev', 'web-dev', 'automotive'] },
      { value: 'B', text: 'Advancing technology and innovation', traits: ['data-science', 'aerospace', 'algorithms-research'] },
      { value: 'C', text: 'Serving society and nation', traits: ['power-systems', 'renewable-energy', 'telecommunications'], category: 'government' },
      { value: 'D', text: 'Building wealth and business', traits: ['web-dev', 'mobile-dev', 'blockchain'], category: 'entrepreneurship' }
    ]
  },
  {
    id: 34,
    category: 'feedback',
    question: 'You prefer feedback through:',
    options: [
      { value: 'A', text: 'Working software and test results', traits: ['software-dev', 'web-dev', 'mobile-dev'] },
      { value: 'B', text: 'Data metrics and analytics', traits: ['data-science', 'cloud-devops', 'power-systems'] },
      { value: 'C', text: 'Physical testing and validation', traits: ['automotive', 'aerospace', 'robotics'] },
      { value: 'D', text: 'Peer review and academic validation', traits: ['algorithms-research', 'materials', 'biomedical-electronics'] }
    ]
  },
  {
    id: 35,
    category: 'long-term',
    question: 'Where do you see yourself in 10 years?',
    options: [
      { value: 'A', text: 'Leading a tech team or department', traits: ['software-dev', 'data-science', 'cloud-devops'], category: 'corporate' },
      { value: 'B', text: 'Running my own successful company', traits: ['web-dev', 'mobile-dev', 'renewable-energy'], category: 'entrepreneurship' },
      { value: 'C', text: 'Senior position in government/PSU', traits: ['power-systems', 'telecommunications', 'aerospace'], category: 'government' },
      { value: 'D', text: 'Professor or research scientist', traits: ['algorithms-research', 'materials', 'aerospace'], category: 'research' }
    ]
  }
];

function CareerAptitudeTest() {
  const navigate = useNavigate();
  const [student, setStudent] = useState(null);
  const [currentSection, setCurrentSection] = useState('intro');
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [results, setResults] = useState(null);
  const [selectedSubdomain, setSelectedSubdomain] = useState(null);
  const [loading, setLoading] = useState(true);

  const studentSession = JSON.parse(sessionStorage.getItem('studentSession') || '{}');
  const rollNumber = studentSession.rollNumber;

  useEffect(() => {
    const loadStudent = async () => {
      if (!rollNumber) {
        navigate('/student-login');
        return;
      }

      try {
        const studentDoc = await getDoc(doc(db, 'students', rollNumber));
        if (studentDoc.exists()) {
          setStudent(studentDoc.data());
        }
      } catch (error) {
        console.error('Error loading student:', error);
      }
      setLoading(false);
    };

    loadStudent();
  }, [rollNumber, navigate]);

  const subdomains = student?.branch ? BRANCH_SUBDOMAINS[student.branch] || [] : [];

  const handleAnswer = (questionId, option) => {
    setAnswers({ ...answers, [questionId]: option });
  };

  const handleNext = () => {
    if (currentQuestion < QUESTIONS.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    }
  };

  const handlePrevious = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
    }
  };

  const calculateResults = () => {
    const subdomainScores = {};
    const categoryScores = {
      corporate: 0,
      government: 0,
      psu: 0,
      entrepreneurship: 0,
      research: 0,
      newAge: 0
    };
    
    // Initialize scores
    subdomains.forEach(subdomain => {
      subdomainScores[subdomain.id] = 0;
    });

    // Calculate scores
    Object.entries(answers).forEach(([qId, answer]) => {
      const question = QUESTIONS.find(q => q.id === parseInt(qId));
      const option = question.options.find(o => o.value === answer);
      
      if (option) {
        // Subdomain scores
        if (option.traits) {
          option.traits.forEach(trait => {
            if (subdomainScores[trait] !== undefined) {
              subdomainScores[trait]++;
            }
          });
        }
        
        // Category scores
        if (option.category) {
          categoryScores[option.category]++;
        }
      }
    });

    // Calculate percentages
    const totalAnswers = Object.keys(answers).length;
    const maxPossibleScore = totalAnswers * 4;
    
    const subdomainResults = Object.entries(subdomainScores)
      .map(([subdomainId, score]) => {
        const subdomain = subdomains.find(s => s.id === subdomainId);
        return {
          subdomain: subdomain,
          score: score,
          percentage: Math.round((score / maxPossibleScore) * 100)
        };
      })
      .filter(result => result.subdomain !== undefined) // Filter out results where subdomain wasn't found
      .sort((a, b) => b.score - a.score);

    // Calculate career path preferences
    const categoryResults = Object.entries(categoryScores)
      .map(([category, score]) => ({
        category: category,
        categoryInfo: CAREER_CATEGORIES[category],
        score: score,
        percentage: Math.round((score / totalAnswers) * 100)
      }))
      .sort((a, b) => b.score - a.score);

    return {
      topSubdomain: subdomainResults[0],
      allSubdomains: subdomainResults,
      careerPathPreferences: categoryResults,
      topCareerPath: categoryResults[0],
      answeredQuestions: totalAnswers,
      timestamp: new Date()
    };
  };

  const handleSubmit = async () => {
    setLoading(true);
    const calculatedResults = calculateResults();
    setResults(calculatedResults);

    try {
      await setDoc(doc(db, 'careerAptitudeTests', rollNumber), {
        rollNumber: rollNumber,
        branch: student.branch,
        responses: Object.entries(answers).map(([qId, answer]) => ({
          questionId: parseInt(qId),
          answer: answer
        })),
        results: calculatedResults,
        completedAt: serverTimestamp()
      });
    } catch (error) {
      console.error('Error saving results:', error);
    }

    setLoading(false);
    setCurrentSection('results');
  };

  const generateSWOT = (subdomain) => {
    return {
      strengths: [
        'Growing demand in the industry',
        'Multiple career opportunities',
        'Good earning potential',
        'Continuous learning and growth'
      ],
      weaknesses: [
        'Requires continuous upskilling',
        'Can be competitive initially',
        'May need additional certifications',
        'Technology changes rapidly'
      ],
      opportunities: [
        'Remote work possibilities',
        'Freelancing opportunities',
        'Global job market access',
        'Entrepreneurship potential'
      ],
      threats: [
        'Automation and AI impact',
        'Market saturation in some areas',
        'Rapid technology obsolescence',
        'Global competition'
      ]
    };
  };

  const generatePDF = async () => {
    // Safety check
    if (!results || !results.topSubdomain || !results.topSubdomain.subdomain || !results.topCareerPath) {
      alert('Unable to generate PDF. Please retake the assessment.');
      return;
    }

    const pdf = new jsPDF();
    
    // Try to dynamically load autoTable if not available
    if (typeof pdf.autoTable !== 'function') {
      try {
        const autoTableModule = await import('jspdf-autotable');
        // Try to attach it
        if (autoTableModule.default) {
          pdf.autoTable = autoTableModule.default.bind(pdf);
        }
      } catch (err) {
        console.error('Failed to load jspdf-autotable:', err);
      }
    }
    
    // Final check
    if (typeof pdf.autoTable !== 'function') {
      alert('PDF table library could not be loaded.\n\nPlease try:\n1. npm install jspdf@2.5.1 jspdf-autotable@3.8.2\n2. Restart your dev server\n3. Clear browser cache');
      console.error('jspdf-autotable not available');
      return;
    }

    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const margin = 20;
    let yPosition = margin;

    // Helper to add text with wrapping
    const addText = (text, size, style = 'normal', color = [0, 0, 0]) => {
      pdf.setFontSize(size);
      pdf.setFont('helvetica', style);
      pdf.setTextColor(...color);
      const lines = pdf.splitTextToSize(text, pageWidth - 2 * margin);
      lines.forEach(line => {
        if (yPosition > pageHeight - margin) {
          pdf.addPage();
          yPosition = margin;
        }
        pdf.text(line, margin, yPosition);
        yPosition += size * 0.5;
      });
      yPosition += 5;
    };

    // Add page check helper
    const checkAddPage = (neededSpace = 60) => {
      if (yPosition > pageHeight - neededSpace) {
        pdf.addPage();
        yPosition = margin;
        return true;
      }
      return false;
    };

    // ===== COVER PAGE =====
    pdf.setFillColor(59, 130, 246);
    pdf.rect(0, 0, pageWidth, pageHeight, 'F');
    
    pdf.setTextColor(255, 255, 255);
    pdf.setFontSize(32);
    pdf.setFont('helvetica', 'bold');
    pdf.text('CAREER APTITUDE', pageWidth / 2, 80, { align: 'center' });
    pdf.text('ASSESSMENT REPORT', pageWidth / 2, 100, { align: 'center' });
    
    pdf.setFontSize(16);
    pdf.setFont('helvetica', 'normal');
    pdf.text(student?.firstName + ' ' + student?.surname, pageWidth / 2, 130, { align: 'center' });
    pdf.text(rollNumber, pageWidth / 2, 145, { align: 'center' });
    pdf.text(student?.branch || '', pageWidth / 2, 160, { align: 'center' });
    
    pdf.setFontSize(12);
    pdf.text('Yashwantrao Bhonsale Institute of Technology', pageWidth / 2, 220, { align: 'center' });
    pdf.text(new Date().toLocaleDateString(), pageWidth / 2, 235, { align: 'center' });

    // ===== PAGE 2: EXECUTIVE SUMMARY =====
    pdf.addPage();
    yPosition = margin;
    
    addText('EXECUTIVE SUMMARY', 20, 'bold', [59, 130, 246]);
    addText(`This comprehensive career aptitude assessment analyzed your responses to 35 carefully designed questions covering work preferences, technical interests, career aspirations, and personality traits. The assessment evaluated ${subdomains.length} potential career paths within ${student?.branch || 'your branch'} to identify those that best align with your natural inclinations and professional goals.`, 10);
    
    yPosition += 5;
    pdf.setDrawColor(200, 200, 200);
    pdf.line(margin, yPosition, pageWidth - margin, yPosition);
    yPosition += 10;

    // Top Recommendation Box
    pdf.setFillColor(240, 253, 244);
    pdf.rect(margin, yPosition, pageWidth - 2 * margin, 70, 'F');
    yPosition += 10;
    addText('YOUR TOP CAREER MATCH', 16, 'bold', [22, 163, 74]);
    addText(results.topSubdomain.subdomain.name, 14, 'bold');
    addText(`Match Score: ${results.topSubdomain.percentage}%`, 12, 'bold', [22, 163, 74]);
    addText(results.topSubdomain.subdomain.description, 10);
    yPosition += 10;

    // Career Path Preference
    checkAddPage();
    addText('YOUR CAREER PATH PREFERENCE', 14, 'bold', [59, 130, 246]);
    addText(`Based on your responses, you show strongest alignment with: ${results.topCareerPath.categoryInfo.name}`, 10);
    yPosition += 5;

    // ===== PAGE 3: DETAILED CAREER PATH ANALYSIS =====
    pdf.addPage();
    yPosition = margin;
    addText('DETAILED CAREER PATH ANALYSIS', 18, 'bold', [59, 130, 246]);
    yPosition += 5;

    results.allSubdomains.slice(0, 5).forEach((result, index) => {
      checkAddPage(80);
      const subdomain = result.subdomain;
      
      addText(`${index + 1}. ${subdomain.name} (${result.percentage}% Match)`, 13, 'bold');
      addText(subdomain.details, 9);
      addText('Key Skills: ' + subdomain.skills.join(', '), 9, 'italic');
      addText('Career Roles: ' + subdomain.careerPaths.join(', '), 9);
      yPosition += 3;
      pdf.setDrawColor(230, 230, 230);
      pdf.line(margin, yPosition, pageWidth - margin, yPosition);
      yPosition += 8;
    });

    // ===== PAGE 4: SWOT ANALYSIS =====
    pdf.addPage();
    yPosition = margin;
    addText('SWOT ANALYSIS', 18, 'bold', [59, 130, 246]);
    addText(`For ${results.topSubdomain.subdomain.name}`, 12, 'bold');
    yPosition += 5;

    const swot = generateSWOT(results.topSubdomain.subdomain);
    
    // Create SWOT table
    pdf.autoTable({
      startY: yPosition,
      head: [['Strengths', 'Weaknesses']],
      body: [
        [swot.strengths.join('\n'), swot.weaknesses.join('\n')]
      ],
      theme: 'grid',
      headStyles: { fillColor: [22, 163, 74] },
      margin: { left: margin, right: margin }
    });

    yPosition = pdf.lastAutoTable.finalY + 10;
    
    pdf.autoTable({
      startY: yPosition,
      head: [['Opportunities', 'Threats']],
      body: [
        [swot.opportunities.join('\n'), swot.threats.join('\n')]
      ],
      theme: 'grid',
      headStyles: { fillColor: [59, 130, 246] },
      margin: { left: margin, right: margin }
    });

    // ===== PAGE 5: CAREER PATHWAYS =====
    pdf.addPage();
    yPosition = margin;
    addText('CAREER PATHWAYS & OPPORTUNITIES', 18, 'bold', [59, 130, 246]);
    yPosition += 5;

    const topSubdomain = results.topSubdomain.subdomain;

    // Corporate/Private Sector
    checkAddPage();
    addText('🏢 Corporate & Private Sector', 12, 'bold', [79, 70, 229]);
    topSubdomain.careerPaths.forEach(path => {
      addText(`• ${path}`, 9);
    });
    yPosition += 5;

    // Government Jobs
    checkAddPage();
    addText('🏛️ Government Opportunities', 12, 'bold', [220, 38, 38]);
    topSubdomain.governmentJobs.forEach(job => {
      addText(`• ${job}`, 9);
    });
    yPosition += 5;

    // PSUs
    checkAddPage();
    addText('🏭 Public Sector Undertakings (PSUs)', 12, 'bold', [234, 88, 12]);
    topSubdomain.psus.forEach(psu => {
      addText(`• ${psu}`, 9);
    });
    yPosition += 5;

    // Entrepreneurship
    checkAddPage();
    addText('🚀 Entrepreneurship Opportunities', 12, 'bold', [16, 185, 129]);
    topSubdomain.entrepreneurship.forEach(opp => {
      addText(`• ${opp}`, 9);
    });
    yPosition += 5;

    // New Age Opportunities
    checkAddPage();
    addText('⚡ New Age & Emerging Opportunities', 12, 'bold', [147, 51, 234]);
    topSubdomain.newAge.forEach(tech => {
      addText(`• ${tech}`, 9);
    });
    yPosition += 5;

    // ===== PAGE 6-7: ACTIONABLE ROADMAP =====
    pdf.addPage();
    yPosition = margin;
    addText('YOUR PERSONALIZED CAREER ROADMAP', 18, 'bold', [59, 130, 246]);
    yPosition += 5;

    // Immediate Steps
    checkAddPage(60);
    pdf.setFillColor(240, 253, 244);
    pdf.rect(margin, yPosition, pageWidth - 2 * margin, 8, 'F');
    yPosition += 6;
    addText('PHASE 1: Immediate Steps (0-3 Months)', 12, 'bold', [22, 163, 74]);
    getImmediateSteps(results.topSubdomain.subdomain.id).forEach((step, idx) => {
      addText(`${idx + 1}. ${step}`, 9);
    });
    yPosition += 10;

    // Short-term Steps
    checkAddPage(60);
    pdf.setFillColor(254, 243, 199);
    pdf.rect(margin, yPosition, pageWidth - 2 * margin, 8, 'F');
    yPosition += 6;
    addText('PHASE 2: Short-term Goals (3-6 Months)', 12, 'bold', [234, 179, 8]);
    getShortTermSteps(results.topSubdomain.subdomain.id).forEach((step, idx) => {
      addText(`${idx + 1}. ${step}`, 9);
    });
    yPosition += 10;

    // Long-term Steps
    checkAddPage(60);
    pdf.setFillColor(254, 226, 226);
    pdf.rect(margin, yPosition, pageWidth - 2 * margin, 8, 'F');
    yPosition += 6;
    addText('PHASE 3: Long-term Vision (6-12 Months)', 12, 'bold', [220, 38, 38]);
    getLongTermSteps(results.topSubdomain.subdomain.id).forEach((step, idx) => {
      addText(`${idx + 1}. ${step}`, 9);
    });

    // ===== PAGE 8: EXAM PREPARATION GUIDE =====
    if (results.topCareerPath.category === 'government' || results.topCareerPath.category === 'psu') {
      pdf.addPage();
      yPosition = margin;
      addText('COMPETITIVE EXAM PREPARATION GUIDE', 18, 'bold', [59, 130, 246]);
      yPosition += 5;

      addText('Recommended Exams:', 12, 'bold');
      addText('• GATE (Graduate Aptitude Test in Engineering)', 10);
      addText('• ESE (Engineering Services Examination)', 10);
      addText('• PSU Recruitment through GATE Score', 10);
      addText('• State Government PSC Technical Services', 10);
      yPosition += 10;

      addText('Preparation Strategy:', 12, 'bold');
      addText('1. Start GATE preparation from 3rd year', 10);
      addText('2. Focus on core subjects and mathematics', 10);
      addText('3. Solve previous year question papers', 10);
      addText('4. Join test series for practice', 10);
      addText('5. Balance academics with exam preparation', 10);
      yPosition += 10;

      addText('Top PSUs Recruiting through GATE:', 12, 'bold');
      results.topSubdomain.subdomain.psus.slice(0, 5).forEach(psu => {
        addText(`• ${psu}`, 10);
      });
    }

    // ===== PAGE 9: LEARNING RESOURCES =====
    pdf.addPage();
    yPosition = margin;
    addText('RECOMMENDED LEARNING RESOURCES', 18, 'bold', [59, 130, 246]);
    yPosition += 10;

    addText('Online Learning Platforms:', 12, 'bold', [79, 70, 229]);
    addText('• Coursera - University courses and specializations', 9);
    addText('• Udemy - Practical skill-building courses', 9);
    addText('• NPTEL - Free IIT/IISc courses', 9);
    addText('• edX - MIT, Harvard courses', 9);
    addText('• LinkedIn Learning - Professional development', 9);
    yPosition += 10;

    checkAddPage();
    addText('Certification Platforms:', 12, 'bold', [22, 163, 74]);
    addText('• Google Cloud/AWS/Azure - Cloud certifications', 9);
    addText('• CompTIA - IT and cybersecurity certifications', 9);
    addText('• Cisco - Networking certifications', 9);
    addText('• PMP - Project management certification', 9);
    yPosition += 10;

    checkAddPage();
    addText('Practice Platforms:', 12, 'bold', [234, 179, 8]);
    addText('• LeetCode/HackerRank - Coding practice', 9);
    addText('• Kaggle - Data science competitions', 9);
    addText('• GitHub - Build portfolio', 9);
    addText('• Stack Overflow - Learn from community', 9);
    yPosition += 10;

    checkAddPage();
    addText('Government Exam Resources:', 12, 'bold', [220, 38, 38]);
    addText('• GATE Academy - Test series and classes', 9);
    addText('• Made Easy - GATE/ESE preparation', 9);
    addText('• Previous Year GATE/ESE papers', 9);
    addText('• Standard textbooks for core subjects', 9);

    // ===== PAGE 10: NETWORKING & MENTORSHIP =====
    pdf.addPage();
    yPosition = margin;
    addText('NETWORKING & MENTORSHIP', 18, 'bold', [59, 130, 246]);
    yPosition += 10;

    addText('Professional Networks:', 12, 'bold');
    addText('• LinkedIn - Build professional profile', 9);
    addText('• GitHub - Showcase your projects', 9);
    addText('• Twitter - Follow industry leaders', 9);
    addText('• Reddit - Join relevant communities', 9);
    yPosition += 10;

    checkAddPage();
    addText('How to Find Mentors:', 12, 'bold');
    addText('• Connect with YBIT alumni in your field', 9);
    addText('• Attend industry conferences and meetups', 9);
    addText('• Participate in online communities', 9);
    addText('• Reach out to professionals on LinkedIn', 9);
    addText('• Join professional organizations', 9);
    yPosition += 10;

    checkAddPage();
    addText('Building Your Brand:', 12, 'bold');
    addText('• Create a strong LinkedIn profile', 9);
    addText('• Write technical blogs', 9);
    addText('• Contribute to open source', 9);
    addText('• Speak at college events', 9);
    addText('• Build a portfolio website', 9);

    // ===== PAGE 11: SALARY & GROWTH PROSPECTS =====
    pdf.addPage();
    yPosition = margin;
    addText('SALARY & GROWTH PROSPECTS', 18, 'bold', [59, 130, 246]);
    yPosition += 10;

    addText('Expected Salary Ranges (in LPA):', 12, 'bold');
    addText(`For ${results.topSubdomain.subdomain.name}:`, 10, 'italic');
    yPosition += 5;

    pdf.autoTable({
      startY: yPosition,
      head: [['Career Stage', 'Corporate', 'PSU/Govt', 'Startup']],
      body: [
        ['Entry Level (0-2 years)', '3-8 LPA', '6-10 LPA', '3-12 LPA'],
        ['Mid Level (3-5 years)', '8-15 LPA', '10-15 LPA', '8-20 LPA'],
        ['Senior Level (6-10 years)', '15-30 LPA', '15-25 LPA', '15-40 LPA'],
        ['Expert Level (10+ years)', '25-60 LPA', '20-35 LPA', '30-100+ LPA']
      ],
      theme: 'grid',
      headStyles: { fillColor: [59, 130, 246] },
      margin: { left: margin, right: margin }
    });

    yPosition = pdf.lastAutoTable.finalY + 15;
    checkAddPage();
    
    addText('Growth Trajectory:', 12, 'bold');
    addText('• Year 1-2: Junior Engineer/Developer', 9);
    addText('• Year 3-5: Senior Engineer/Technical Lead', 9);
    addText('• Year 6-8: Engineering Manager/Architect', 9);
    addText('• Year 9+: Director/VP Engineering/CTO', 9);

    // ===== FINAL PAGE: IMPORTANT NOTES =====
    pdf.addPage();
    yPosition = margin;
    
    pdf.setFillColor(254, 226, 226);
    pdf.rect(0, 0, pageWidth, 60, 'F');
    yPosition = 25;
    addText('IMPORTANT REMINDERS', 16, 'bold', [220, 38, 38]);
    yPosition = 40;
    addText('This assessment is a guide, not a limitation. Your career path can evolve!', 11, 'normal', [220, 38, 38]);
    
    yPosition = 70;
    addText('Key Takeaways:', 14, 'bold');
    addText('1. Explore multiple domains through projects and internships', 10);
    addText('2. Don\'t limit yourself to just the top recommendation', 10);
    addText('3. Skills are transferable across domains', 10);
    addText('4. Stay curious and keep learning continuously', 10);
    addText('5. Network with professionals in your areas of interest', 10);
    addText('6. Build a strong portfolio showcasing your work', 10);
    addText('7. Seek mentorship from industry experts', 10);
    addText('8. Consider both technical and soft skills development', 10);
    yPosition += 15;

    addText('Next Steps:', 14, 'bold');
    addText('□ Review your top 3 career matches', 10);
    addText('□ Start with immediate action steps this week', 10);
    addText('□ Connect with alumni in your chosen field', 10);
    addText('□ Begin building relevant skills', 10);
    addText('□ Research companies/organizations you want to join', 10);
    addText('□ Create a 6-month learning plan', 10);
    addText('□ Schedule regular progress reviews', 10);
    yPosition += 15;

    pdf.setFillColor(240, 253, 244);
    pdf.rect(margin, yPosition, pageWidth - 2 * margin, 40, 'F');
    yPosition += 10;
    addText('Your Future is Bright!', 12, 'bold', [22, 163, 74]);
    addText('Remember: Success comes from consistent effort, continuous learning, and genuine passion for your work. Use this report as your roadmap, but don\'t hesitate to explore and adapt as you grow.', 9);
    
    yPosition = pageHeight - 20;
    pdf.setFontSize(8);
    pdf.setTextColor(150, 150, 150);
    pdf.text(`YBIT Career Guidance System | Generated on ${new Date().toLocaleDateString()}`, pageWidth / 2, yPosition, { align: 'center' });

    // Save PDF
    pdf.save(`Career_Aptitude_Report_${rollNumber}_${new Date().getTime()}.pdf`);
  };

  // Helper functions remain the same as previous version
  const getImmediateSteps = (subdomainId) => {
    const steps = {
      'software-dev': [
        'Start learning a programming language (Python or JavaScript recommended)',
        'Complete at least 20 coding problems on platforms like HackerRank',
        'Build your first simple project (calculator, to-do list)',
        'Set up a GitHub account and learn basic Git commands',
        'Join online communities like r/learnprogramming'
      ],
      'data-science': [
        'Learn Python basics and install Jupyter Notebook',
        'Take a free course on statistics fundamentals',
        'Work with your first dataset using Pandas library',
        'Learn basic data visualization with Matplotlib',
        'Join Kaggle and explore beginner datasets'
      ],
      'power-systems': [
        'Review electrical fundamentals (circuit theory, transformers)',
        'Learn about power system components and protection',
        'Study GATE previous year questions on Power Systems',
        'Install and learn MATLAB/Simulink basics',
        'Follow PSU recruitment notifications'
      ],
      'automotive': [
        'Learn about vehicle systems and components',
        'Study thermodynamics and fluid mechanics basics',
        'Explore CAD software (AutoCAD or SolidWorks basics)',
        'Follow automotive industry news and trends',
        'Learn about EV technology and regulations'
      ],
      'aerospace': [
        'Study aerodynamics fundamentals',
        'Learn about aircraft systems and structures',
        'Follow ISRO and DRDO updates and careers',
        'Explore CATIA or ANSYS basics',
        'Join aerospace engineering forums'
      ]
      // Add more as needed
    };
    return steps[subdomainId] || [
      'Explore introductory courses in your chosen field',
      'Connect with professionals on LinkedIn',
      'Join relevant online communities and forums',
      'Start building basic projects or case studies',
      'Read industry blogs and publications'
    ];
  };

  const getShortTermSteps = (subdomainId) => {
    const steps = {
      'software-dev': [
        'Build 2-3 medium-sized projects with different technologies',
        'Learn a web framework (React, Django, or similar)',
        'Contribute to open-source projects on GitHub',
        'Start a technical blog documenting your learning',
        'Attend tech meetups or webinars'
      ],
      'data-science': [
        'Complete a comprehensive data science course (Coursera/edX)',
        'Learn machine learning fundamentals',
        'Work on 2-3 end-to-end ML projects',
        'Master NumPy, Pandas, Scikit-learn',
        'Participate in Kaggle competitions'
      ],
      'power-systems': [
        'Prepare seriously for GATE exam',
        'Study for PSU recruitment exams',
        'Learn power system simulation software (PSSE, ETAP)',
        'Work on power system analysis projects',
        'Apply for relevant internships at power companies'
      ],
      'automotive': [
        'Learn advanced CAD/CAM software proficiently',
        'Study vehicle dynamics and design',
        'Complete automotive industry certifications',
        'Work on automotive design projects',
        'Apply for internships at automotive companies'
      ],
      'aerospace': [
        'Prepare for ISRO/DRDO exams if interested',
        'Learn aerospace simulation tools (ANSYS, CFD)',
        'Work on aerodynamics or propulsion projects',
        'Join model rocketry or UAV clubs',
        'Apply for internships at aerospace organizations'
      ]
    };
    return steps[subdomainId] || [
      'Complete advanced courses and certifications',
      'Build substantial portfolio projects',
      'Gain practical experience through internships',
      'Network with industry professionals',
      'Stay updated with latest industry trends'
    ];
  };

  const getLongTermSteps = (subdomainId) => {
    const steps = {
      'software-dev': [
        'Develop expertise in specific technology stack',
        'Contribute significantly to major open-source projects',
        'Build and maintain strong professional portfolio',
        'Apply for full-time positions or internships',
        'Consider specializing in high-demand areas (Cloud, AI, etc.)'
      ],
      'data-science': [
        'Develop domain expertise (healthcare, finance, etc.)',
        'Build portfolio of impactful data science projects',
        'Master deep learning and neural networks',
        'Publish findings or create case studies',
        'Apply for data science positions or research roles'
      ],
      'power-systems': [
        'Clear GATE with good score for PSU recruitment',
        'Prepare for ESE for government engineering services',
        'Gain experience through power sector internships',
        'Consider M.Tech in Power Systems from top IITs',
        'Apply for positions in NTPC, PGCIL, state electricity boards'
      ],
      'automotive': [
        'Master automotive design and analysis tools',
        'Gain expertise in specific automotive domain (EV, design, etc.)',
        'Build impressive automotive project portfolio',
        'Apply for positions at major automotive companies',
        'Consider higher studies in automotive engineering'
      ],
      'aerospace': [
        'Prepare thoroughly for ISRO/DRDO recruitment',
        'Build strong aerospace engineering portfolio',
        'Consider M.Tech/MS in Aerospace from top institutions',
        'Gain experience in aerospace projects or internships',
        'Apply for positions at HAL, ISRO, DRDO, private aerospace firms'
      ]
    };
    return steps[subdomainId] || [
      'Develop professional-level expertise in your field',
      'Build impressive and diverse portfolio',
      'Apply for competitive positions',
      'Consider higher studies if interested in research',
      'Build strong professional network'
    ];
  };

  // UI rendering code continues as before...
  // (Keep the same UI structure as the previous version)

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Brain className="w-16 h-16 text-blue-600 mx-auto mb-4 animate-pulse" />
          <p className="text-gray-600">Loading assessment...</p>
        </div>
      </div>
    );
  }

  // Introduction Screen
  if (currentSection === 'intro') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 py-12">
        <div className="max-w-4xl mx-auto px-4">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center space-x-2 text-blue-600 hover:text-blue-800 mb-6"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Back</span>
          </button>

          <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12">
            <div className="text-center mb-8">
              <div className="w-20 h-20 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-full flex items-center justify-center mx-auto mb-6">
                <Brain className="w-10 h-10 text-white" />
              </div>
              <h1 className="text-4xl font-bold text-gray-900 mb-4">
                Comprehensive Career Aptitude Assessment
              </h1>
              <p className="text-xl text-gray-600">
                Discover Your Ideal Career Path in {student?.branch || 'Engineering'}
              </p>
            </div>

            <div className="space-y-6 mb-8">
              <div className="bg-blue-50 border-l-4 border-blue-600 p-4 rounded">
                <div className="flex items-start">
                  <Info className="w-6 h-6 text-blue-600 mr-3 flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2">What You'll Discover</h3>
                    <p className="text-gray-700">
                      This comprehensive assessment analyzes your aptitude across {subdomains.length} specialized career paths, evaluating your fit for Corporate, Government, PSU, Entrepreneurship, Teaching & Research, and New Age opportunities.
                    </p>
                  </div>
                </div>
              </div>

              <div className="grid md:grid-cols-3 gap-4">
                <div className="bg-gradient-to-br from-blue-500 to-indigo-600 text-white p-4 rounded-lg">
                  <div className="text-3xl font-bold mb-1">35</div>
                  <div className="text-sm">Carefully Designed Questions</div>
                </div>
                <div className="bg-gradient-to-br from-green-500 to-emerald-600 text-white p-4 rounded-lg">
                  <div className="text-3xl font-bold mb-1">{subdomains.length}</div>
                  <div className="text-sm">Career Paths Analyzed</div>
                </div>
                <div className="bg-gradient-to-br from-purple-500 to-pink-600 text-white p-4 rounded-lg">
                  <div className="text-3xl font-bold mb-1">6</div>
                  <div className="text-sm">Career Categories</div>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                {Object.entries(CAREER_CATEGORIES).map(([key, category]) => (
                  <div key={key} className="flex items-start space-x-3 bg-gray-50 p-4 rounded-lg">
                    <span className="text-2xl">{category.icon}</span>
                    <div>
                      <h4 className="font-semibold text-gray-900">{category.name}</h4>
                      <p className="text-gray-600 text-sm">{category.description}</p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="bg-gradient-to-r from-indigo-50 to-purple-50 p-6 rounded-lg">
                <h3 className="font-semibold text-gray-900 mb-3">Your Comprehensive Report Will Include:</h3>
                <div className="grid md:grid-cols-2 gap-3 text-sm text-gray-700">
                  <div className="flex items-start">
                    <CheckCircle className="w-4 h-4 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Ranked career path matches with percentages</span>
                  </div>
                  <div className="flex items-start">
                    <CheckCircle className="w-4 h-4 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span>SWOT analysis for your top career match</span>
                  </div>
                  <div className="flex items-start">
                    <CheckCircle className="w-4 h-4 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Corporate, Government, PSU opportunities</span>
                  </div>
                  <div className="flex items-start">
                    <CheckCircle className="w-4 h-4 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Entrepreneurship possibilities</span>
                  </div>
                  <div className="flex items-start">
                    <CheckCircle className="w-4 h-4 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Teaching & research pathways</span>
                  </div>
                  <div className="flex items-start">
                    <CheckCircle className="w-4 h-4 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span>New age and emerging opportunities</span>
                  </div>
                  <div className="flex items-start">
                    <CheckCircle className="w-4 h-4 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span>3-phase actionable roadmap</span>
                  </div>
                  <div className="flex items-start">
                    <CheckCircle className="w-4 h-4 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Salary expectations & growth prospects</span>
                  </div>
                  <div className="flex items-start">
                    <CheckCircle className="w-4 h-4 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Exam preparation guide (for Govt/PSU)</span>
                  </div>
                  <div className="flex items-start">
                    <CheckCircle className="w-4 h-4 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Learning resources & certifications</span>
                  </div>
                  <div className="flex items-start">
                    <CheckCircle className="w-4 h-4 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Networking & mentorship strategies</span>
                  </div>
                  <div className="flex items-start">
                    <CheckCircle className="w-4 h-4 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Professional PDF report to download</span>
                  </div>
                </div>
              </div>

              <div className="bg-yellow-50 border-l-4 border-yellow-500 p-4 rounded">
                <p className="text-sm text-gray-700">
                  <strong>Remember:</strong> This assessment takes approximately 20-25 minutes. There are no right or wrong answers—just honest preferences that help us understand you better. Your responses are confidential and used only for generating your personalized career guidance report.
                </p>
              </div>
            </div>

            <div className="flex justify-center">
              <button
                onClick={() => setCurrentSection('education')}
                className="px-10 py-4 bg-gradient-to-r from-blue-600 to-indigo-600 text-white text-lg font-semibold rounded-lg hover:from-blue-700 hover:to-indigo-700 transition-all shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
              >
                Begin Assessment
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Rest of the sections (education, test, results) remain similar to previous version
  // with updated career categories and PDF generation
  
  // Education Screen
  if (currentSection === 'education') {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-6xl mx-auto px-4">
          <button
            onClick={() => setCurrentSection('intro')}
            className="flex items-center space-x-2 text-blue-600 hover:text-blue-800 mb-6"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Back</span>
          </button>

          <div className="bg-white rounded-lg shadow-md p-8 mb-6">
            <h1 className="text-3xl font-bold text-gray-900 mb-4">
              Explore Career Paths in {student?.branch || 'Your Branch'}
            </h1>
            <p className="text-gray-600 mb-6">
              Before we begin the assessment, let's explore the different career paths available to you. Click on each domain to learn more.
            </p>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
              {subdomains.map(subdomain => (
                <div
                  key={subdomain.id}
                  className={`border-2 rounded-lg p-4 cursor-pointer transition-all ${
                    selectedSubdomain?.id === subdomain.id
                      ? 'border-blue-600 bg-blue-50 shadow-lg'
                      : 'border-gray-200 hover:border-blue-300 hover:shadow-md'
                  }`}
                  onClick={() => setSelectedSubdomain(subdomain)}
                >
                  <h3 className="font-bold text-gray-900 mb-2">{subdomain.name}</h3>
                  <p className="text-gray-600 text-sm line-clamp-2">{subdomain.description}</p>
                </div>
              ))}
            </div>

            {selectedSubdomain && (
              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-6 mb-6">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">{selectedSubdomain.name}</h2>
                <p className="text-gray-700 mb-6">{selectedSubdomain.details}</p>
                
                <div className="grid md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                      <Target className="w-4 h-4 mr-2 text-blue-600" />
                      Key Skills
                    </h4>
                    <ul className="space-y-2">
                      {selectedSubdomain.skills.map((skill, idx) => (
                        <li key={idx} className="flex items-center text-gray-700 text-sm">
                          <CheckCircle className="w-3 h-3 text-green-600 mr-2" />
                          {skill}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                      <Briefcase className="w-4 h-4 mr-2 text-purple-600" />
                      Career Roles
                    </h4>
                    <ul className="space-y-2">
                      {selectedSubdomain.careerPaths.slice(0, 4).map((career, idx) => (
                        <li key={idx} className="flex items-center text-gray-700 text-sm">
                          <CheckCircle className="w-3 h-3 text-purple-600 mr-2" />
                          {career}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div className="bg-white rounded-lg p-4">
                    <h5 className="font-semibold text-sm text-gray-900 mb-2 flex items-center">
                      <Building2 className="w-4 h-4 mr-2 text-orange-600" />
                      Government/PSU
                    </h5>
                    <p className="text-xs text-gray-600">{selectedSubdomain.governmentJobs.slice(0, 2).join(', ')}</p>
                  </div>
                  <div className="bg-white rounded-lg p-4">
                    <h5 className="font-semibold text-sm text-gray-900 mb-2 flex items-center">
                      <Rocket className="w-4 h-4 mr-2 text-green-600" />
                      Entrepreneurship
                    </h5>
                    <p className="text-xs text-gray-600">{selectedSubdomain.entrepreneurship.slice(0, 2).join(', ')}</p>
                  </div>
                </div>
              </div>
            )}

            <div className="flex justify-center">
              <button
                onClick={() => setCurrentSection('test')}
                className="px-8 py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-colors shadow-md"
              >
                Start Assessment →
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Test Screen
  if (currentSection === 'test') {
    const currentQ = QUESTIONS[currentQuestion];
    const progress = ((currentQuestion + 1) / QUESTIONS.length) * 100;
    const isLastQuestion = currentQuestion === QUESTIONS.length - 1;
    const currentAnswer = answers[currentQ.id];

    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-3xl mx-auto px-4">
          {/* Progress Bar */}
          <div className="mb-8">
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm font-medium text-gray-700">
                Question {currentQuestion + 1} of {QUESTIONS.length}
              </span>
              <span className="text-sm font-medium text-blue-600">{Math.round(progress)}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-3">
              <div
                className="bg-gradient-to-r from-blue-600 to-indigo-600 h-3 rounded-full transition-all duration-300"
                style={{ width: `${progress}%` }}
              ></div>
            </div>
          </div>

          {/* Question Card */}
          <div className="bg-white rounded-xl shadow-lg p-8">
            <div className="mb-6">
              <span className="inline-block px-4 py-1.5 bg-blue-100 text-blue-800 text-sm font-semibold rounded-full mb-4">
                {currentQ.category.replace('-', ' ').toUpperCase()}
              </span>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">{currentQ.question}</h2>
            </div>

            <div className="space-y-3 mb-8">
              {currentQ.options.map(option => (
                <button
                  key={option.value}
                  onClick={() => handleAnswer(currentQ.id, option.value)}
                  className={`w-full text-left p-5 rounded-xl border-2 transition-all ${
                    currentAnswer === option.value
                      ? 'border-blue-600 bg-blue-50 shadow-md transform scale-[1.02]'
                      : 'border-gray-200 hover:border-blue-300 hover:bg-gray-50 hover:shadow-sm'
                  }`}
                >
                  <div className="flex items-start">
                    <span className="font-bold text-lg text-blue-600 mr-4">{option.value}</span>
                    <span className="text-gray-700 leading-relaxed">{option.text}</span>
                  </div>
                </button>
              ))}
            </div>

            {/* Navigation */}
            <div className="flex justify-between items-center pt-4 border-t">
              <button
                onClick={handlePrevious}
                disabled={currentQuestion === 0}
                className={`px-6 py-2.5 rounded-lg font-medium transition-all ${
                  currentQuestion === 0
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                ← Previous
              </button>

              {isLastQuestion ? (
                <button
                  onClick={handleSubmit}
                  disabled={!currentAnswer || loading}
                  className={`px-8 py-2.5 rounded-lg font-semibold transition-all ${
                    !currentAnswer || loading
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : 'bg-gradient-to-r from-green-600 to-emerald-600 text-white hover:from-green-700 hover:to-emerald-700 shadow-md'
                  }`}
                >
                  {loading ? 'Analyzing...' : 'Complete Assessment ✓'}
                </button>
              ) : (
                <button
                  onClick={handleNext}
                  disabled={!currentAnswer}
                  className={`px-8 py-2.5 rounded-lg font-semibold transition-all ${
                    !currentAnswer
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white hover:from-blue-700 hover:to-indigo-700 shadow-md'
                  }`}
                >
                  Next →
                </button>
              )}
            </div>
          </div>

          {/* Progress Indicator */}
          <div className="mt-6 text-center text-sm text-gray-600">
            {Math.round(progress)}% Complete • {QUESTIONS.length - currentQuestion - 1} questions remaining
          </div>
        </div>
      </div>
    );
  }

  // Results Screen
  if (currentSection === 'results' && results) {
    // Safety check: ensure we have valid results
    if (!results.topSubdomain || !results.topSubdomain.subdomain || !results.topCareerPath) {
      return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-md p-8 max-w-md text-center">
            <div className="text-red-500 mb-4">
              <Info className="w-16 h-16 mx-auto" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Unable to Generate Results</h2>
            <p className="text-gray-600 mb-6">
              We encountered an issue calculating your career profile. This might be due to incomplete responses or a technical error.
            </p>
            <button
              onClick={() => {
                setCurrentSection('test');
                setCurrentQuestion(0);
              }}
              className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Retake Assessment
            </button>
          </div>
        </div>
      );
    }

    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 py-12">
        <div className="max-w-7xl mx-auto px-4">
          <div className="bg-white rounded-2xl shadow-2xl p-8 md:p-12">
            {/* Header */}
            <div className="text-center mb-12">
              <div className="w-24 h-24 bg-gradient-to-br from-green-500 to-emerald-600 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg">
                <CheckCircle className="w-14 h-14 text-white" />
              </div>
              <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
                Your Comprehensive Career Profile
              </h1>
              <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                Based on your responses to 35 questions, here's your personalized career guidance
              </p>
            </div>

            {/* Career Path Preference */}
            <div className="bg-gradient-to-r from-purple-500 to-pink-600 rounded-xl p-6 text-white mb-8">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm font-semibold mb-2 opacity-90">YOUR CAREER PATH PREFERENCE</div>
                  <div className="text-3xl font-bold">{results.topCareerPath.categoryInfo.name}</div>
                  <div className="mt-2 text-purple-100">{results.topCareerPath.categoryInfo.description}</div>
                </div>
                <div className="text-6xl">{results.topCareerPath.categoryInfo.icon}</div>
              </div>
            </div>

            {/* Top Recommendation */}
            <div className="bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl p-8 text-white mb-8 shadow-xl">
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <span className="inline-block px-4 py-1.5 bg-white/20 rounded-full text-sm font-semibold mb-4">
                    🏆 TOP TECHNICAL DOMAIN MATCH
                  </span>
                  <h2 className="text-3xl font-bold mb-3">{results.topSubdomain.subdomain.name}</h2>
                  <p className="text-blue-100 text-lg mb-4">{results.topSubdomain.subdomain.description}</p>
                  <p className="text-blue-50 text-sm">{results.topSubdomain.subdomain.details}</p>
                </div>
                <div className="text-right ml-6">
                  <div className="text-6xl font-bold mb-2">{results.topSubdomain.percentage}%</div>
                  <div className="text-blue-200 font-medium">Match Score</div>
                </div>
              </div>
            </div>

            {/* Career Opportunities Grid */}
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
              {[
                { key: 'careerPaths', icon: <Briefcase className="w-5 h-5" />, title: 'Corporate Roles', color: 'blue' },
                { key: 'governmentJobs', icon: <Building2 className="w-5 h-5" />, title: 'Government', color: 'red' },
                { key: 'psus', icon: <Building2 className="w-5 h-5" />, title: 'PSUs', color: 'orange' },
                { key: 'entrepreneurship', icon: <Rocket className="w-5 h-5" />, title: 'Entrepreneurship', color: 'green' },
                { key: 'newAge', icon: <TrendingUp className="w-5 h-5" />, title: 'New Age', color: 'purple' },
                { key: 'skills', icon: <Target className="w-5 h-5" />, title: 'Key Skills', color: 'indigo' }
              ].map((item) => (
                <div key={item.key} className={`bg-${item.color}-50 border-2 border-${item.color}-200 rounded-lg p-4`}>
                  <div className={`flex items-center space-x-2 mb-3 text-${item.color}-700`}>
                    {item.icon}
                    <h3 className="font-bold">{item.title}</h3>
                  </div>
                  <ul className="space-y-1 text-sm text-gray-700">
                    {results.topSubdomain.subdomain[item.key].slice(0, 3).map((opp, idx) => (
                      <li key={idx} className="flex items-start">
                        <span className={`text-${item.color}-600 mr-1`}>•</span>
                        <span className="line-clamp-2">{opp}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>

            {/* All Career Matches */}
            <div className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                <Target className="w-6 h-6 mr-3 text-blue-600" />
                All Career Path Matches
              </h2>
              <div className="grid md:grid-cols-2 gap-4">
                {results.allSubdomains.slice(0, 8).map((result, index) => (
                  <div
                    key={result.subdomain.id}
                    className="border-2 border-gray-200 rounded-lg p-4 hover:border-blue-300 transition-all hover:shadow-md"
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-start space-x-3 flex-1">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-white flex-shrink-0 ${
                          index === 0 ? 'bg-yellow-500' : index === 1 ? 'bg-gray-400' : index === 2 ? 'bg-orange-500' : 'bg-blue-500'
                        }`}>
                          {index + 1}
                        </div>
                        <div className="flex-1">
                          <h3 className="font-bold text-gray-900 mb-1">{result.subdomain.name}</h3>
                          <p className="text-gray-600 text-xs line-clamp-2">{result.subdomain.description}</p>
                        </div>
                      </div>
                      <div className="text-right ml-3">
                        <div className="text-2xl font-bold text-blue-600">{result.percentage}%</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Quick Action Steps */}
            <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-8 mb-8 border-2 border-green-200">
              <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                <TrendingUp className="w-6 h-6 mr-3 text-green-600" />
                Your Personalized Roadmap
              </h2>
              
              <div className="space-y-6">
                <div>
                  <h3 className="font-bold text-green-800 mb-3 flex items-center">
                    <span className="w-8 h-8 bg-green-600 text-white rounded-full flex items-center justify-center mr-3 text-sm font-bold">1</span>
                    Start This Week
                  </h3>
                  <ul className="ml-11 space-y-2">
                    {getImmediateSteps(results.topSubdomain.subdomain.id).slice(0, 3).map((step, idx) => (
                      <li key={idx} className="flex items-start text-gray-700 text-sm">
                        <CheckCircle className="w-4 h-4 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                        <span>{step}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div>
                  <h3 className="font-bold text-blue-800 mb-3 flex items-center">
                    <span className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center mr-3 text-sm font-bold">2</span>
                    Next 3 Months
                  </h3>
                  <ul className="ml-11 space-y-2">
                    {getShortTermSteps(results.topSubdomain.subdomain.id).slice(0, 3).map((step, idx) => (
                      <li key={idx} className="flex items-start text-gray-700 text-sm">
                        <TrendingUp className="w-4 h-4 text-blue-600 mr-2 mt-0.5 flex-shrink-0" />
                        <span>{step}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div>
                  <h3 className="font-bold text-purple-800 mb-3 flex items-center">
                    <span className="w-8 h-8 bg-purple-600 text-white rounded-full flex items-center justify-center mr-3 text-sm font-bold">3</span>
                    Long-term Vision
                  </h3>
                  <ul className="ml-11 space-y-2">
                    {getLongTermSteps(results.topSubdomain.subdomain.id).slice(0, 3).map((step, idx) => (
                      <li key={idx} className="flex items-start text-gray-700 text-sm">
                        <Target className="w-4 h-4 text-purple-600 mr-2 mt-0.5 flex-shrink-0" />
                        <span>{step}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>

            {/* Download & Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-8">
              <button
                onClick={generatePDF}
                className="flex items-center space-x-3 px-8 py-4 bg-gradient-to-r from-red-600 to-pink-600 text-white text-lg font-semibold rounded-lg hover:from-red-700 hover:to-pink-700 transition-all shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
              >
                <Download className="w-6 h-6" />
                <span>Download Complete Report (PDF)</span>
              </button>
              
              <button
                onClick={() => navigate('/student/dashboard')}
                className="px-8 py-4 bg-gray-200 text-gray-700 text-lg font-semibold rounded-lg hover:bg-gray-300 transition-colors"
              >
                Return to Dashboard
              </button>
            </div>

            {/* Important Note */}
            <div className="bg-blue-50 border-l-4 border-blue-600 rounded p-6">
              <div className="flex items-start">
                <Info className="w-6 h-6 text-blue-600 mr-3 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm text-gray-700 leading-relaxed">
                    <strong className="text-blue-900">Important:</strong> This assessment is a comprehensive guide to help you explore career possibilities. Your interests and skills will evolve with experience and learning. We recommend:
                  </p>
                  <ul className="mt-2 space-y-1 text-sm text-gray-700">
                    <li>• Explore multiple domains through projects and internships</li>
                    <li>• Network with professionals in your areas of interest</li>
                    <li>• Download the PDF report for detailed roadmaps and resources</li>
                    <li>• Regularly update your skills based on industry trends</li>
                    <li>• Seek mentorship from faculty and industry experts</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return null;
}

export default CareerAptitudeTest;
